/**
 * L-Shaped Sectional Cushion Configurator — Pricing Engine + Interactive SVG Preview
 * uscushion.com · 2026-07
 *
 * L-shape = single piece with a rectangular notch cut from one corner.
 * 5 dimensions: Back Width, Front Width, Left Length, Right Length, Thickness
 * Shape selection: RHS (notch at right-front) or LHS (notch at left-front)
 * Pricing: L-shape area formula (bounding rectangle minus notch)
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench) ─── */
  var FABRIC_PRICE_RMB  = 25;
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    extraCovers: { '0': 0, '1': 30, '2': 50 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };

  var LABELS = {
    none: 'None', single: 'Single piping', double: 'Double piping',
    match: 'Match fabric', white: 'White piping', black: 'Black piping', navy: 'Navy piping', cream: 'Cream piping',
    two: '2 straps', four: '4 straps',
    regular: 'Regular zipper', invisible: 'Invisible zipper',
    velcro: 'Velcro', black_pad: 'Black pad', beige_pad: 'Beige pad'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    lshapeDir: '',
    backW: 0, frontW: 0, leftL: 0, rightL: 0, thickness: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Pricing (L-shape: single piece cut from one foam block) ───
   * Foam is carved from a full rectangular block (bw × ll), the notch
   * offcut is scrapped, so foam MATERIAL cost bills the whole block
   * (bounding rect, notch NOT deducted). Shipping weight uses the net
   * L area — only the finished piece is shipped, not the scrap. Fabric
   * top/bottom follow the net shape (flat offcut is nestable/reusable).
   */
  function calcLShapePrice(bw, fw, ll, rl, t) {
    if (!bw || !fw || !ll || !rl || !t) return null;
    if (bw <= fw || ll <= rl) return null;

    var bwM = bw / 39.37, fwM = fw / 39.37, llM = ll / 39.37, rlM = rl / 39.37, tM = t / 39.37;

    var notchW = bwM - fwM;
    var notchL = llM - rlM;
    var netAreaM2 = bwM * llM - notchW * notchL;   // finished L area (fabric + shipping weight)

    var perimeterM = 2 * bwM + 2 * llM;
    var fabricArea = 2 * netAreaM2 + perimeterM * tM;
    var foamVolumeCost = bwM * llM * tM;            // material: full block consumed, notch scrapped
    var foamVolumeNet  = netAreaM2 * tM;            // shipping: only the finished L is shipped
    var totalWeight = foamVolumeNet * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * FABRIC_PRICE_RMB + foamVolumeCost * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / TARGET_COST_RATIO * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + (ADDON.extraCovers[state.extraCovers] || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0) + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasDims() {
    return state.backW >= 6 && state.frontW >= 6 && state.leftL >= 6 && state.rightL >= 6 && state.thickness >= 1
      && state.backW > state.frontW && state.leftL > state.rightL;
  }

  function isComplete() {
    return state.lshapeDir && hasDims()
      && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — Isometric L-shape cushion
     ═══════════════════════════════════════════════════════════ */

  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    var bw = state.backW, fw = state.frontW, ll = state.leftL, rl = state.rightL, t = state.thickness;
    var isRHS = state.lshapeDir !== 'lhs';
    var empty = (!bw && !fw && !ll && !rl && !t);

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    /* ── Resolve face colors from fabric ── */
    var topC = '#EDE9E0', frontC = '#D5D0C2', sideC = '#E4E0D4', innerC = '#DDD8CA', strokeC = '#2A2A28';
    var fabricLum = 200;
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50);
        sideC = lighten(baseHex, 0.35);
        frontC = lighten(baseHex, 0.20);
        innerC = lighten(baseHex, 0.28);
        strokeC = darken(baseHex, 0.45);
        var sc = hexToRgb(strokeC);
        fabricLum = (sc[0] + sc[1] + sc[2]) / 3;
        if (fabricLum > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex);
        fabricLum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }

    var h = '';

    /* ── Defs ── */
    h += '<defs><marker id="ls-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    /* ── Isometric direction vectors ── */
    var dirHoriz = [0.98776, -0.15596];
    var dirDiag  = [-0.35120, -0.93660];

    /* ── Compute pixel sizes ── */
    var widPx, lenPx, thickH, fwRatio, rlRatio;
    if (empty) {
      widPx = 110; lenPx = 130; thickH = 14; fwRatio = 0.6; rlRatio = 0.5;
    } else {
      var ubw = bw || 60, ufw = fw || 36, ull = ll || 60, url_ = rl || 30, ut = t || 3;
      var aspect = ubw / ull;
      widPx = clamp(130 / (1 + aspect * 0.2), 60, 150);
      lenPx = clamp(widPx * (ull / ubw), 60, 170);
      thickH = clamp(ut * 4, 8, 30);
      fwRatio = clamp(ufw / ubw, 0.3, 0.9);
      rlRatio = clamp(url_ / ull, 0.2, 0.9);
    }

    var fwPx = widPx * fwRatio;
    var rlPx = lenPx * rlRatio;

    var origin = [280, 260];

    /*
     * L-shape top face vertices (RHS — notch at right-front):
     *
     *   B ─────────────── C (back edge, full width)
     *   |                 |
     *   |                 | rightL
     *   |                 |
     *   |        E ────── D (inner notch corner)
     *   |        |
     *   | leftL  | leftL - rightL
     *   |        |
     *   A ────── F (front edge, front width)
     *      frontW
     *
     * For LHS, mirror horizontally (notch at left-front)
     */

    var vA, vB, vC, vD, vE, vF;

    if (isRHS) {
      vA = origin;
      vF = [vA[0] + dirHoriz[0] * fwPx, vA[1] + dirHoriz[1] * fwPx];
      vB = [vA[0] + dirDiag[0] * lenPx, vA[1] + dirDiag[1] * lenPx];
      vC = [vB[0] + dirHoriz[0] * widPx, vB[1] + dirHoriz[1] * widPx];
      vD = [vB[0] + dirHoriz[0] * widPx + dirDiag[0] * (-(lenPx - rlPx)), vB[1] + dirHoriz[1] * widPx + dirDiag[1] * (-(lenPx - rlPx))];
      vE = [vA[0] + dirHoriz[0] * fwPx + dirDiag[0] * (lenPx - (lenPx - rlPx)), vA[1] + dirHoriz[1] * fwPx + dirDiag[1] * (lenPx - (lenPx - rlPx))];
    } else {
      // LHS: notch at left-front. Full width starts from origin going right.
      // The front portion is on the right side.
      vA = origin;
      vB = [vA[0] + dirDiag[0] * (lenPx - rlPx), vA[1] + dirDiag[1] * (lenPx - rlPx)];
      var cornerBL = [vA[0] + dirDiag[0] * lenPx, vA[1] + dirDiag[1] * lenPx];
      vC = [cornerBL[0] + dirHoriz[0] * widPx, cornerBL[1] + dirHoriz[1] * widPx];
      vD = [vC[0] - dirDiag[0] * lenPx, vC[1] - dirDiag[1] * lenPx];
      vF = [vD[0] - dirHoriz[0] * fwPx, vD[1] - dirHoriz[1] * fwPx];
      vE = [vF[0] + dirDiag[0] * (lenPx - rlPx), vF[1] + dirDiag[1] * (lenPx - rlPx)];

      // Reassign for LHS: A=top-left notch corner, B=inner notch, E=inner horizontal, F=front-right
      // Actually let me redo this more carefully for LHS
      // For LHS mirror: the shape is the same but the notch is on the left-front
      var o = origin;
      // Back-right corner (full width to the right)
      var pBackRight = [o[0] + dirHoriz[0] * widPx, o[1] + dirHoriz[1] * widPx];
      // Back-left corner
      var pBackLeft = [o[0] + dirDiag[0] * lenPx, o[1] + dirDiag[1] * lenPx];
      // Back-left + full width = back-right (going along back)
      var pBackLeftFar = [pBackLeft[0] + dirHoriz[0] * widPx, pBackLeft[1] + dirHoriz[1] * widPx];
      // Front-right corner (full length from back-right)
      var pFrontRight = [pBackRight[0] - dirDiag[0] * lenPx + dirDiag[0] * lenPx, pBackRight[1] - dirDiag[1] * lenPx + dirDiag[1] * lenPx];

      // Let me use a cleaner approach: define all 6 vertices
      // Going clockwise from front-left:
      // For LHS the front-right side has full length, left side has short length
      vA = o; // front-left (notch corner — but this is actually the short side)
      // Wait, for LHS the notch is at the LEFT-front. So:
      // The RIGHT side has the full leftL length, LEFT side has rightL length
      // Let me re-think the vertex naming for consistency

      // I'll just mirror the RHS by swapping width direction
      vD = o; // front-right (full corner)
      vC = [o[0] + dirDiag[0] * lenPx, o[1] + dirDiag[1] * lenPx]; // back-right
      vB = [vC[0] + dirHoriz[0] * widPx, vC[1] + dirHoriz[1] * widPx]; // back-left (in iso, horiz goes right)

      // Hmm this is getting confused. Let me simplify.
    }

    // Simplify: always compute as RHS, then for LHS just mirror the width axis
    // RHS notch: back-right area is cut. In isometric view:

    // Clean approach: define in 2D plan view then project to isometric
    // Plan coordinates (x=width going right, y=length going up/back):
    // RHS vertices (clockwise from front-left):
    //   P0 = (0, 0)           front-left
    //   P1 = (fw, 0)          front-right (front width)
    //   P2 = (fw, ll-rl)      inner notch corner
    //   P3 = (bw, ll-rl)      outer notch corner (= right side at rightL from front)
    //   P4 = (bw, ll)         back-right
    //   P5 = (0, ll)          back-left

    // For LHS, mirror: replace x with (bw - x)
    //   P0 = (bw, 0)          front-right
    //   P1 = (bw-fw, 0)       front-left
    //   P2 = (bw-fw, ll-rl)   inner notch corner
    //   P3 = (0, ll-rl)       outer notch corner
    //   P4 = (0, ll)          back-left
    //   P5 = (bw, ll)         back-right

    // Isometric projection: isoX = x * dirHoriz + y * dirDiag
    function isoProject(px, py) {
      return [
        origin[0] + px * dirHoriz[0] + py * dirDiag[0],
        origin[1] + px * dirHoriz[1] + py * dirDiag[1]
      ];
    }

    var planVerts;
    if (isRHS) {
      planVerts = [
        [0, 0],
        [fwPx, 0],
        [fwPx, lenPx - rlPx],
        [widPx, lenPx - rlPx],
        [widPx, lenPx],
        [0, lenPx]
      ];
    } else {
      planVerts = [
        [widPx, 0],
        [widPx - fwPx, 0],
        [widPx - fwPx, lenPx - rlPx],
        [0, lenPx - rlPx],
        [0, lenPx],
        [widPx, lenPx]
      ];
    }

    // Project all top vertices
    var top = planVerts.map(function (p) { return isoProject(p[0], p[1]); });
    // Bottom vertices (shifted down by thickH)
    var bot = top.map(function (p) { return [p[0], p[1] + thickH]; });

    /* ── Draw faces using painter's algorithm ── */

    // Determine which faces are visible based on the isometric view direction
    // In our isometric view (dirHoriz points right-down, dirDiag points left-up):
    // Visible faces: top, front (bottom edge), left side, and any inner notch faces

    // For RHS:
    // Front face: P0-P1 edge (bottom of shape)
    // Left face: P5-P0 edge
    // Right face of front portion: P1-P2 edge
    // Inner notch horizontal: P2-P3 edge (faces forward/down)
    // Right face of back portion: P3-P4 edge (partially visible)

    // For LHS:
    // Front face: P0-P1 edge (P0 is right, P1 is left — reversed)
    // Right face: P0-P5 edge
    // Left face of front portion: P1-P2 edge
    // Inner notch horizontal: P2-P3 edge
    // Left face of back portion: P3-P4 edge

    // Draw back faces first, then front faces, then top

    if (isRHS) {
      // 1. Right side face (P3-P4, back portion) — furthest back
      h += '<polygon points="' + [top[3], top[4], bot[4], bot[3]].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

      // 2. Inner notch horizontal face (P2-P3, faces downward in view)
      h += '<polygon points="' + [top[2], top[3], bot[3], bot[2]].map(pt).join(' ') + '" fill="' + innerC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

      // 3. Left side face (P5-P0)
      h += '<polygon points="' + [top[5], top[0], bot[0], bot[5]].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

      // 4. Right side of front portion (P1-P2)
      h += '<polygon points="' + [top[1], top[2], bot[2], bot[1]].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

      // 5. Front face (P0-P1)
      h += '<polygon points="' + [top[0], top[1], bot[1], bot[0]].map(pt).join(' ') + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    } else {
      // LHS: mirror drawing order
      // 1. Left side face (P3-P4, back portion)
      h += '<polygon points="' + [top[3], top[4], bot[4], bot[3]].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

      // 2. Inner notch horizontal face (P2-P3)
      h += '<polygon points="' + [top[2], top[3], bot[3], bot[2]].map(pt).join(' ') + '" fill="' + innerC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

      // 3. Right side face (P5-P0)
      h += '<polygon points="' + [top[5], top[0], bot[0], bot[5]].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

      // 4. Left side of front portion (P1-P2)
      h += '<polygon points="' + [top[1], top[2], bot[2], bot[1]].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

      // 5. Front face (P1-P0, note reversed for LHS)
      h += '<polygon points="' + [top[1], top[0], bot[0], bot[1]].map(pt).join(' ') + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    }

    // 6. Top face (L-shape polygon)
    h += '<polygon points="' + top.map(pt).join(' ') + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    /* ── Piping ── */
    if (state.piping === 'single' || state.piping === 'double') {
      var pipC = '#F5F1E8', pw = 2.2;
      // Top edge piping
      h += '<polygon points="' + top.map(pt).join(' ') + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      if (state.piping === 'double') {
        // Bottom edge piping (visible front + sides)
        if (isRHS) {
          h += '<polyline points="' + [bot[5], bot[0], bot[1], bot[2], bot[3]].map(pt).join(' ')
            + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
        } else {
          h += '<polyline points="' + [bot[5], bot[0], bot[1], bot[2], bot[3]].map(pt).join(' ')
            + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
        }
      }
    }

    /* ── Anti-slip ── */
    if (state.antislip && state.antislip !== 'none' && !empty) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }

      // Draw a simplified anti-slip indicator on the front face
      var padInset = 4;
      var normalDir = [0, 1];
      var frontLeft = bot[isRHS ? 0 : 1];
      var frontRight = bot[isRHS ? 1 : 0];
      var edgeLen = Math.sqrt(Math.pow(frontRight[0] - frontLeft[0], 2) + Math.pow(frontRight[1] - frontLeft[1], 2));

      if (state.antislip === 'velcro') {
        var strips = 4, gapPx = edgeLen / strips;
        for (var vi = 0; vi < strips; vi++) {
          var ratio0 = (vi * gapPx + 2) / edgeLen;
          var ratio1 = ((vi + 1) * gapPx - 2) / edgeLen;
          var s0 = [frontLeft[0] + (frontRight[0] - frontLeft[0]) * ratio0, frontLeft[1] + (frontRight[1] - frontLeft[1]) * ratio0];
          var s1 = [frontLeft[0] + (frontRight[0] - frontLeft[0]) * ratio1, frontLeft[1] + (frontRight[1] - frontLeft[1]) * ratio1];
          var s2 = [s1[0], s1[1] + 4];
          var s3 = [s0[0], s0[1] + 4];
          h += '<polygon points="' + [s0, s1, s2, s3].map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
        }
      } else {
        var ratio0 = padInset / edgeLen;
        var ratio1 = 1 - padInset / edgeLen;
        var s0 = [frontLeft[0] + (frontRight[0] - frontLeft[0]) * ratio0, frontLeft[1] + (frontRight[1] - frontLeft[1]) * ratio0];
        var s1 = [frontLeft[0] + (frontRight[0] - frontLeft[0]) * ratio1, frontLeft[1] + (frontRight[1] - frontLeft[1]) * ratio1];
        var s2 = [s1[0], s1[1] + 4];
        var s3 = [s0[0], s0[1] + 4];
        h += '<polygon points="' + [s0, s1, s2, s3].map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
      }
    }

    /* ── Straps ── */
    if ((state.straps === 'two' || state.straps === 'four') && !empty) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47';
      var hangLen = 14;
      function drawTies(cx_, cy_, dirX) {
        var spread = 4;
        h += '<path d="M' + f(cx_ - spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ - spread + dirX * 3) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ - spread * 1.5 + dirX * 5) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(cx_ + spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ + spread + dirX * 2) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ + spread * 1.5 + dirX * 4) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }
      drawTies(bot[0][0], bot[0][1], -1);
      drawTies(bot[1][0], bot[1][1], 1);
      if (state.straps === 'four') {
        drawTies(bot[4][0], bot[4][1], -1);
        drawTies(bot[isRHS ? 3 : 3][0], bot[isRHS ? 3 : 3][1], 1);
      }
    }

    /* ── Dimension labels ── */
    var dimC = '#1a4f78', thkC = '#3d5e3f';
    var ls = 10;
    var hug = 20;

    var lblBW = bw ? ' — ' + bw + '″' : '';
    var lblFW = fw ? ' — ' + fw + '″' : '';
    var lblLL = ll ? ' — ' + ll + '″' : '';
    var lblRL = rl ? ' — ' + rl + '″' : '';
    var lblT  = t  ? ' — ' + t + '″' : '';

    if (isRHS) {
      // Back Width — along back edge (P5-P4), top
      var bw1 = [top[5][0] + dirDiag[0] * 12, top[5][1] + dirDiag[1] * 12];
      var bw2 = [top[4][0] + dirDiag[0] * 12, top[4][1] + dirDiag[1] * 12];
      h += '<line x1="' + f(top[5][0]) + '" y1="' + f(top[5][1]) + '" x2="' + f(bw1[0]) + '" y2="' + f(bw1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(top[4][0]) + '" y1="' + f(top[4][1]) + '" x2="' + f(bw2[0]) + '" y2="' + f(bw2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bw1[0]) + '" y1="' + f(bw1[1]) + '" x2="' + f(bw2[0]) + '" y2="' + f(bw2[1])
        + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ls-arr)" marker-end="url(#ls-arr)"/>';
      h += '<text x="' + f((bw1[0] + bw2[0]) / 2) + '" y="' + f((bw1[1] + bw2[1]) / 2 - 5) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Back Width' + lblBW + '</text>';

      // Front Width — along front edge (P0-P1)
      var fw1 = [bot[0][0] + hug * 0.5, bot[0][1] + hug * 0.6];
      var fw2 = [bot[1][0] + hug * 0.5, bot[1][1] + hug * 0.6];
      h += '<line x1="' + f(bot[0][0]) + '" y1="' + f(bot[0][1]) + '" x2="' + f(fw1[0]) + '" y2="' + f(fw1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bot[1][0]) + '" y1="' + f(bot[1][1]) + '" x2="' + f(fw2[0]) + '" y2="' + f(fw2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(fw1[0]) + '" y1="' + f(fw1[1]) + '" x2="' + f(fw2[0]) + '" y2="' + f(fw2[1])
        + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ls-arr)" marker-end="url(#ls-arr)"/>';
      h += '<text x="' + f((fw1[0] + fw2[0]) / 2) + '" y="' + f((fw1[1] + fw2[1]) / 2 + 14) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Front Width' + lblFW + '</text>';

      // Left Length — along left side (P0-P5)
      var ll1 = [bot[0][0] - hug * 0.8, bot[0][1] + hug * 0.2];
      var ll2 = [bot[5][0] - hug * 0.8, bot[5][1] + hug * 0.2];
      h += '<line x1="' + f(bot[0][0]) + '" y1="' + f(bot[0][1]) + '" x2="' + f(ll1[0]) + '" y2="' + f(ll1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bot[5][0]) + '" y1="' + f(bot[5][1]) + '" x2="' + f(ll2[0]) + '" y2="' + f(ll2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(ll1[0]) + '" y1="' + f(ll1[1]) + '" x2="' + f(ll2[0]) + '" y2="' + f(ll2[1])
        + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ls-arr)" marker-end="url(#ls-arr)"/>';
      h += '<text x="' + f((ll1[0] + ll2[0]) / 2 - 6) + '" y="' + f((ll1[1] + ll2[1]) / 2 + 4) + '" text-anchor="end" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Left Length' + lblLL + '</text>';

      // Right Length — along right side (P3-P4)
      var rl1 = [top[3][0] + hug * 0.6, top[3][1] - hug * 0.1];
      var rl2 = [top[4][0] + hug * 0.6, top[4][1] - hug * 0.1];
      h += '<line x1="' + f(top[3][0]) + '" y1="' + f(top[3][1]) + '" x2="' + f(rl1[0]) + '" y2="' + f(rl1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(top[4][0]) + '" y1="' + f(top[4][1]) + '" x2="' + f(rl2[0]) + '" y2="' + f(rl2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(rl1[0]) + '" y1="' + f(rl1[1]) + '" x2="' + f(rl2[0]) + '" y2="' + f(rl2[1])
        + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ls-arr)" marker-end="url(#ls-arr)"/>';
      h += '<text x="' + f((rl1[0] + rl2[0]) / 2 + 5) + '" y="' + f((rl1[1] + rl2[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Right Length' + lblRL + '</text>';

    } else {
      // LHS labels — mirrored positions
      // Back Width — along back edge (P4-P5)
      var bw1 = [top[4][0] + dirDiag[0] * 12, top[4][1] + dirDiag[1] * 12];
      var bw2 = [top[5][0] + dirDiag[0] * 12, top[5][1] + dirDiag[1] * 12];
      h += '<line x1="' + f(top[4][0]) + '" y1="' + f(top[4][1]) + '" x2="' + f(bw1[0]) + '" y2="' + f(bw1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(top[5][0]) + '" y1="' + f(top[5][1]) + '" x2="' + f(bw2[0]) + '" y2="' + f(bw2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bw1[0]) + '" y1="' + f(bw1[1]) + '" x2="' + f(bw2[0]) + '" y2="' + f(bw2[1])
        + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ls-arr)" marker-end="url(#ls-arr)"/>';
      h += '<text x="' + f((bw1[0] + bw2[0]) / 2) + '" y="' + f((bw1[1] + bw2[1]) / 2 - 5) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Back Width' + lblBW + '</text>';

      // Front Width — along front edge (P1-P0)
      var fw1 = [bot[1][0] + hug * 0.5, bot[1][1] + hug * 0.6];
      var fw2 = [bot[0][0] + hug * 0.5, bot[0][1] + hug * 0.6];
      h += '<line x1="' + f(bot[1][0]) + '" y1="' + f(bot[1][1]) + '" x2="' + f(fw1[0]) + '" y2="' + f(fw1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bot[0][0]) + '" y1="' + f(bot[0][1]) + '" x2="' + f(fw2[0]) + '" y2="' + f(fw2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(fw1[0]) + '" y1="' + f(fw1[1]) + '" x2="' + f(fw2[0]) + '" y2="' + f(fw2[1])
        + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ls-arr)" marker-end="url(#ls-arr)"/>';
      h += '<text x="' + f((fw1[0] + fw2[0]) / 2) + '" y="' + f((fw1[1] + fw2[1]) / 2 + 14) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Front Width' + lblFW + '</text>';

      // Right Length — along right side (P0-P5, full length)
      var ll1 = [bot[5][0] + hug * 0.6, bot[5][1] - hug * 0.1];
      var ll2 = [bot[0][0] + hug * 0.6, bot[0][1] - hug * 0.1];
      h += '<line x1="' + f(bot[5][0]) + '" y1="' + f(bot[5][1]) + '" x2="' + f(ll1[0]) + '" y2="' + f(ll1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bot[0][0]) + '" y1="' + f(bot[0][1]) + '" x2="' + f(ll2[0]) + '" y2="' + f(ll2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(ll1[0]) + '" y1="' + f(ll1[1]) + '" x2="' + f(ll2[0]) + '" y2="' + f(ll2[1])
        + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ls-arr)" marker-end="url(#ls-arr)"/>';
      h += '<text x="' + f((ll1[0] + ll2[0]) / 2 + 5) + '" y="' + f((ll1[1] + ll2[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Right Length' + lblRL + '</text>';

      // Left Length — along left side (P3-P4)
      var rl1 = [bot[3][0] - hug * 0.8, bot[3][1] + hug * 0.2];
      var rl2 = [bot[4][0] - hug * 0.8, bot[4][1] + hug * 0.2];
      h += '<line x1="' + f(bot[3][0]) + '" y1="' + f(bot[3][1]) + '" x2="' + f(rl1[0]) + '" y2="' + f(rl1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bot[4][0]) + '" y1="' + f(bot[4][1]) + '" x2="' + f(rl2[0]) + '" y2="' + f(rl2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(rl1[0]) + '" y1="' + f(rl1[1]) + '" x2="' + f(rl2[0]) + '" y2="' + f(rl2[1])
        + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ls-arr)" marker-end="url(#ls-arr)"/>';
      h += '<text x="' + f((rl1[0] + rl2[0]) / 2 - 6) + '" y="' + f((rl1[1] + rl2[1]) / 2 + 4) + '" text-anchor="end" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Left Length' + lblLL + '</text>';
    }

    // Thickness — vertical line on the rightmost visible front corner
    var thIdx = isRHS ? 1 : 0;
    var thTop = top[thIdx];
    var thBot = bot[thIdx];
    var thx = thBot[0] + hug;
    h += '<line x1="' + f(thTop[0]) + '" y1="' + f(thTop[1]) + '" x2="' + f(thx) + '" y2="' + f(thTop[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thBot[0]) + '" y1="' + f(thBot[1]) + '" x2="' + f(thx) + '" y2="' + f(thBot[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thx) + '" y1="' + f(thTop[1] + 2) + '" x2="' + f(thx) + '" y2="' + f(thBot[1] - 2) + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#ls-arr)" marker-end="url(#ls-arr)"/>';
    h += '<text x="' + f(thx + 5) + '" y="' + f((thTop[1] + thBot[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC + '">Thickness' + lblT + '</text>';

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    var dir = state.lshapeDir === 'rhs' ? 'RHS' : state.lshapeDir === 'lhs' ? 'LHS' : '';
    if (dir) parts.push('L-Shape ' + dir);
    if (state.backW && state.frontW && state.leftL && state.rightL && state.thickness)
      parts.push(state.backW + '×' + state.frontW + '×' + state.leftL + '×' + state.rightL + '×' + state.thickness + '″');
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pipLabel = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pipLabel += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pipLabel);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Configure your L-shaped cushion below';
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;
    state.backW     = parseFloat(els.backW.value) || 0;
    state.frontW    = parseFloat(els.frontW.value) || 0;
    state.thickness = parseFloat(els.thickness.value) || 0;
    state.leftL     = parseFloat(els.leftL.value) || 0;
    state.rightL    = parseFloat(els.rightL.value) || 0;
    state.extraCovers = els.extraCovers ? els.extraCovers.value : '0';
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) {
        state.pipingColor = 'match';
        if (els.pipingColor) els.pipingColor.value = 'match';
      }
    }

    // Validation highlights
    ['backW', 'frontW', 'thickness', 'leftL', 'rightL'].forEach(function (id) {
      if (els[id]) els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });

    // Validate constraints: backW > frontW, leftL > rightL
    var constraintOk = true;
    if (state.backW > 0 && state.frontW > 0 && state.backW <= state.frontW) {
      constraintOk = false;
      els.frontW.style.borderColor = '#c44';
    } else if (els.frontW) {
      els.frontW.style.borderColor = '';
    }
    if (state.leftL > 0 && state.rightL > 0 && state.leftL <= state.rightL) {
      constraintOk = false;
      els.rightL.style.borderColor = '#c44';
    } else if (els.rightL) {
      els.rightL.style.borderColor = '';
    }

    drawCushion();
    updateSummary();

    var dimsOk = hasDims();
    var ec = parseInt(state.extraCovers) || 0;
    var basePrice = dimsOk ? calcLShapePrice(state.backW, state.frontW, state.leftL, state.rightL, state.thickness) : null;
    var addonTotal = getAddonTotal();

    if (basePrice !== null) {
      var finalPrice = Math.round(basePrice + addonTotal);
      els.priceMain.textContent = '$' + finalPrice;
      var parts = ['L-Shape $' + Math.round(basePrice)];
      if (ADDON.piping[state.piping])     parts.push('Piping +$' + ADDON.piping[state.piping]);
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Piping color +$' + ADDON.pipingColor[state.pipingColor]);
      if (ADDON.straps[state.straps])     parts.push('Straps +$' + ADDON.straps[state.straps]);
      if (ADDON.zipper[state.zipper])     parts.push('Zipper +$' + ADDON.zipper[state.zipper]);
      if (ADDON.antislip[state.antislip]) parts.push('Anti-slip +$' + ADDON.antislip[state.antislip]);
      if (ADDON.extraCovers[ec])          parts.push('Covers +$' + ADDON.extraCovers[ec]);
      if (ADDON.throwPillow[state.throwPillow]) parts.push('Throw Pillow +$' + ADDON.throwPillow[state.throwPillow]);
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('Pillow Covers +$' + ADDON.pillowCovers[state.pillowCovers]);
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armrest +$' + ADDON.armrestPillow[state.armrestPillow]);
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '$—';
      if (!constraintOk) {
        els.priceBreak.textContent = 'Back Width must > Front Width, Left Length must > Right Length';
      } else {
        els.priceBreak.textContent = 'Enter dimensions to see price';
      }
    }

    var complete = isComplete();
    var btns = [els.addBtn, els.addBtnSticky].filter(Boolean);
    btns.forEach(function (btn) {
      btn.disabled = !complete;
      if (complete && basePrice !== null) {
        var fp = Math.round(basePrice + addonTotal);
        btn.textContent = 'Add to order — $' + fp + ' · Free shipping →';
      } else {
        btn.textContent = 'Add to order →';
      }
    });

    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Free shipping · Ships in ~20 business days';
      } else {
        var missing = [];
        if (!state.lshapeDir) missing.push('shape direction');
        if (!dimsOk) {
          if (!constraintOk) {
            missing.push('valid dimensions (Back Width > Front Width, Left Length > Right Length)');
          } else {
            missing.push('all 5 dimensions');
          }
        }
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? 'Select ' + missing.join(', ') + ' to continue' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: {
        price: (basePrice !== null) ? Math.round(basePrice + addonTotal) : null,
        state: state
      }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var basePrice = calcLShapePrice(state.backW, state.frontW, state.leftL, state.rightL, state.thickness);
    var finalPrice = Math.round(basePrice + getAddonTotal());
    var dirLabel = state.lshapeDir === 'rhs' ? 'RHS (right-hand side)' : 'LHS (left-hand side)';
    var props = {
      'Shape': 'L-Shaped Sectional — ' + dirLabel,
      'Back Width': state.backW + '"',
      'Front Width': state.frontW + '"',
      'Left Length': state.leftL + '"',
      'Right Length': state.rightL + '"',
      'Thickness': state.thickness + '"',
      'Piping': LABELS[state.piping] || state.piping,
      'Piping Color': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Match fabric',
      'Straps': LABELS[state.straps] || state.straps,
      'Zipper': LABELS[state.zipper] || state.zipper,
      'Anti-slip': LABELS[state.antislip] || state.antislip
    };
    if (state.fabric) {
      props['Fabric'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
    }
    var ec = parseInt(state.extraCovers) || 0;
    if (ec > 0) props['Extra Covers'] = ec + ' set(s)';
    if (state.throwPillow !== 'none') props['Throw Pillow'] = state.throwPillow.replace('pcs_', ' pcs ');
    if (state.pillowCovers !== 'none') props['Pillow Covers'] = state.pillowCovers.replace('pcs_', ' pcs ');
    if (state.armrestPillow !== 'none') props['Armrest Pillow'] = state.armrestPillow.replace('_', ' ');
    if (state.zipperPlacement) {
      var ZIP_PLACEMENT_LABELS = { A: 'A — Left side', B: 'B — Back edge', C: 'C — Right side', D: 'D — Front edge (facing you)' };
      props['Zipper Placement'] = ZIP_PLACEMENT_LABELS[state.zipperPlacement] || state.zipperPlacement;
    }
    if (state.notes.trim()) props['Customer Note'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    return { properties: props, price: finalPrice };
  }

  /* ─── DOM transformation (adapt shared Liquid section for L-Shape) ─── */
  function transformDOM() {
    // Show back dims section (reuse for Left Length + Right Length)
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) {
      var el = backDims[i];
      el.style.display = el.classList.contains('cc-sp-dims') ? 'grid' : 'block';
    }

    // Relabel seat inputs: Length → Back Width, Width → Front Width, Thickness stays
    var labelL = document.querySelector('label[for="cc-dim-l"]');
    if (labelL) labelL.innerHTML = 'Back Width (in) <span class="cc-req">*</span>';
    var inputL = document.getElementById('cc-dim-l');
    if (inputL) { inputL.placeholder = 'e.g. 60'; inputL.max = '120'; }

    var labelW = document.querySelector('label[for="cc-dim-w"]');
    if (labelW) labelW.innerHTML = 'Front Width (in) <span class="cc-req">*</span>';
    var inputW = document.getElementById('cc-dim-w');
    if (inputW) inputW.placeholder = 'e.g. 36';

    // Relabel back inputs: Length → Left Length, Width → Right Length, hide back Thickness
    var labelBL = document.querySelector('label[for="cc-back-l"]');
    if (labelBL) labelBL.innerHTML = 'Left Length (in) <span class="cc-req">*</span>';
    var inputBL = document.getElementById('cc-back-l');
    if (inputBL) inputBL.placeholder = 'e.g. 60';

    var labelBW = document.querySelector('label[for="cc-back-w"]');
    if (labelBW) labelBW.innerHTML = 'Right Length (in) <span class="cc-req">*</span>';
    var inputBW = document.getElementById('cc-back-w');
    if (inputBW) inputBW.placeholder = 'e.g. 30';

    // Hide back thickness (not needed)
    var backT = document.getElementById('cc-back-t');
    if (backT) backT.closest('.cc-field').style.display = 'none';

    // Show section labels
    var seatLabel = document.querySelector('.cc-seat-label');
    if (seatLabel) { seatLabel.style.display = ''; seatLabel.textContent = 'Widths & Thickness'; }
    var backLabel = document.querySelector('.cc-back-label');
    if (backLabel) backLabel.textContent = 'Lengths';

    // Adjust SVG viewBox for L-shape proportions
    var svg = document.getElementById('cc-dim-svg');
    if (svg) {
      svg.setAttribute('viewBox', '0 0 520 330');
      svg.style.maxHeight = '260px';
    }

    // Replace Shape options with RHS / LHS
    var shapeGrid = document.getElementById('cc-shape-grid');
    if (shapeGrid) {
      shapeGrid.classList.remove('g4');
      shapeGrid.classList.add('g2');
      shapeGrid.innerHTML = ''
        + '<div class="cc-opt-card" data-group="lshapeDir" data-value="rhs">'
        +   '<div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/RHS-L_shape_cushion.webp?v=1783583572" alt="L Shape RHS"></div>'
        +   '<div class="cc-opt-name">L Shape — RHS</div><div class="cc-opt-price">included</div>'
        + '</div>'
        + '<div class="cc-opt-card" data-group="lshapeDir" data-value="lhs">'
        +   '<div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/L_Shape_Sectional_Cushion_a_LHS-custom_cushion_500x_f4d0455a-d967-47fc-8ec5-ae35e7943ea1.webp?v=1783583493" alt="L Shape LHS"></div>'
        +   '<div class="cc-opt-name">L Shape — LHS</div><div class="cc-opt-price">included</div>'
        + '</div>';
    }

    // Rename shape section label
    var shapeLabel = document.querySelector('#cc-shape-section .cc-sl');
    if (shapeLabel) shapeLabel.innerHTML = 'Direction <span class="cc-req">*</span>';

    // Hide price area above SVG
    var spTop = document.querySelector('.cc-sp-top');
    if (spTop) spTop.style.display = 'none';
    var priceBreakDiv = document.getElementById('cc-price-break');
    if (priceBreakDiv) priceBreakDiv.style.display = 'none';

    // Tighten label spacing
    var dimsLabels = document.querySelectorAll('.cc-dims-label');
    for (var j = 0; j < dimsLabels.length; j++) {
      dimsLabels[j].style.marginBottom = '0';
      dimsLabels[j].style.paddingBottom = '0';
    }

    // Move dimension inputs out of sticky container
    var stickyWrap = document.getElementById('cc-sticky-preview');
    if (stickyWrap) {
      var dimsContainer = document.createElement('div');
      dimsContainer.className = 'cc-dims-outside';

      var seatLbl = stickyWrap.querySelector('.cc-seat-label');
      var seatDims = stickyWrap.querySelector('.cc-sp-dims:not(.cc-back-dims)');
      var backLbl = stickyWrap.querySelector('.cc-back-label');
      var backDimsEl = stickyWrap.querySelector('.cc-sp-dims.cc-back-dims');
      var hint = stickyWrap.querySelector('.cc-sp-hint');

      if (seatLbl) dimsContainer.appendChild(seatLbl);
      if (seatDims) dimsContainer.appendChild(seatDims);
      if (backLbl) dimsContainer.appendChild(backLbl);
      if (backDimsEl) dimsContainer.appendChild(backDimsEl);
      if (hint) dimsContainer.appendChild(hint);

      stickyWrap.parentNode.insertBefore(dimsContainer, stickyWrap.nextSibling);

      // Add constraint hint below the dimension inputs
      var constraintHint = document.createElement('div');
      constraintHint.style.cssText = 'font-size:11px;color:#8A8A85;padding:4px 16px 8px;line-height:1.4';
      constraintHint.textContent = 'Back Width must be larger than Front Width. Left Length must be larger than Right Length.';
      dimsContainer.appendChild(constraintHint);
    }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    // Mapped inputs: seat L→backW, seat W→frontW, seat T→thickness, back L→leftL, back W→rightL
    els.backW        = document.getElementById('cc-dim-l');
    els.frontW       = document.getElementById('cc-dim-w');
    els.thickness    = document.getElementById('cc-dim-t');
    els.leftL        = document.getElementById('cc-back-l');
    els.rightL       = document.getElementById('cc-back-w');
    els.extraCovers    = document.getElementById('cc-extra-covers');
    els.throwPillow    = document.getElementById('cc-throw-pillow');
    els.pillowCovers   = document.getElementById('cc-pillow-covers');
    els.armrestPillow  = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    ['backW', 'frontW', 'thickness', 'leftL', 'rightL'].forEach(function (id) {
      if (els[id]) els[id].addEventListener('input', recalc);
    });
    if (els.extraCovers) els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) {
      state.fabric = e.detail;
      recalc();
    });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) {
      state.fabric = existingFabric;
      recalc();
    }
    setInterval(function () {
      var f = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (f && f !== state.fabric) {
        state.fabric = f;
        recalc();
      }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.LShapeCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
